import { PathologyRules } from '@quromedical/business-rules';
import { GroupSecurity } from '@quromedical/fhir-common';
import type {
  AdmissionGetResponse,
  PatientCoverageResponse,
  PatientResponse,
} from '@quromedical/models';
import { first } from '@quromedical/utils';
import { WithGroup } from 'core/auth';
import {
  getDataFromResponse,
  serviceRequestColumns,
} from 'core/service-request';
import { PaginatedTable } from 'core/table';
import { Section, Visible } from 'design-system';
import type { FetcherOptions } from 'hooks/usePaginatedData';
import type { UsePathologyActions } from 'hooks/usePathology';
import { useResponsive } from 'hooks/useResponsive';
import { ampathCopyDoctorFetcher } from 'integration';
import {
  AdmissionApi,
  CoverageApi,
  PathologyApi,
  PatientApi,
  PatientEndpoint,
} from 'integration/aggregate';
import type React from 'react';
import { useCallback } from 'react';
import useSWR from 'swr';

import { CoverageStatusCard, ServiceRequestForm } from '../form';

const pathologyApi = new PathologyApi();
const patientApi = new PatientApi();
const admissionApi = new AdmissionApi();
const coverageApi = new CoverageApi();

interface Props {
  patientId: string;
  usePathologyProps: UsePathologyActions;
}

interface ApiData {
  patient: PatientResponse;
  admission?: AdmissionGetResponse;
  coverage?: PatientCoverageResponse;
}

export const RequestTab: React.FC<Props> = ({
  patientId,
  usePathologyProps,
}) => {
  const showHeader = useResponsive({ md: true }, false);
  const isStriped = useResponsive({ md: true }, false);

  const key = patientApi.getPatientSWRKey(
    `${patientId}/admissions`,
    PatientEndpoint.Base,
  );

  const { data } = useSWR(key, async (): Promise<ApiData> => {
    const patientAdmissions = await admissionApi.getAdmissions({
      patient: patientId,
      status: 'in-progress',
    });
    const admission = first(patientAdmissions.admissions);
    const patient = await patientApi.getPatient(patientId);

    const coverage = await coverageApi.getCoverage(patientId);

    return {
      patient,
      admission,
      coverage,
    };
  });

  const hasCoverage = !!data?.coverage;

  const authorizationCode = data?.admission?.medicalAidAuthorization?.code;
  const hasAuthorization = authorizationCode ? authorizationCode !== '' : false;

  const serviceType = data?.admission?.service;

  const validationResult = PathologyRules.validatePathologyRequest({
    serviceType,
    hasMedicalAid: hasCoverage,
    hasMedicalAidAuthorization: hasAuthorization,
  });

  const displayForm = validationResult.type === 'success';
  const errorMessages =
    validationResult.type === 'error' ? validationResult.messages : [];

  const fetcher = (options?: FetcherOptions) =>
    pathologyApi.getPathologyOrders({
      subject: patientId,
      pageToken: options?.nextToken,
      ...options,
    });

  const cacheKey = JSON.stringify({
    subject: patientId,
  });

  const practitionerFetcher = useCallback(
    () => ampathCopyDoctorFetcher(data?.patient),
    [data],
  );

  return (
    <>
      <WithGroup groups={[GroupSecurity.ServiceRequestCreate]}>
        <Visible if={displayForm}>
          <Section>
            <ServiceRequestForm
              practitionerFetcher={practitionerFetcher()}
              patientId={patientId}
              usePathologyProps={usePathologyProps}
            />
          </Section>
        </Visible>
        <Visible if={!displayForm}>
          <Section>
            <CoverageStatusCard messages={errorMessages} />
          </Section>
        </Visible>
      </WithGroup>

      <Section>
        <PaginatedTable
          cacheKey={`ServiceRequest${cacheKey}`}
          fetcher={fetcher}
          columns={serviceRequestColumns}
          showHeader={showHeader}
          isStriped={isStriped}
          getDataFromResponse={getDataFromResponse}
        />
      </Section>
    </>
  );
};
